import { useCallback, useEffect, useState } from "react";
import { Clock, Pencil, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, eur, errMsg, fmtDate, fmtMinutes } from "../lib/api";
import { EmptyState, PageHeader } from "../components/ui-bits";
import { Button } from "../components/ui/button";
import { TimeEntryForm } from "../components/SimpleForms";

export default function TimeTracking() {
  const [entries, setEntries] = useState([]);
  const [summary, setSummary] = useState([]);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [e, s, p] = await Promise.all([
        api.get("/time-entries"),
        api.get("/time-entries/summary"),
        api.get("/projects"),
      ]);
      setEntries(e.data);
      setSummary(s.data);
      setProjects(p.data);
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const remove = async (t) => {
    if (!window.confirm("Zeiteintrag löschen?")) return;
    try {
      await api.delete(`/time-entries/${t.id}`);
      toast.success("Eintrag gelöscht");
      load();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const totalMinutes = entries.reduce((s, t) => s + (t.duration_minutes || 0), 0);

  return (
    <div className="animate-fade-up" data-testid="timetracking-page">
      <PageHeader overline="Arbeitszeit" title="Zeiterfassung" testid="timetracking-header">
        <Button data-testid="new-timeentry-button" onClick={() => { setEditing(null); setFormOpen(true); }} className="rounded-xl bg-[#B88A4A] text-white hover:bg-[#a67a3f]">
          <Plus className="mr-1.5 h-4 w-4" /> Zeit erfassen
        </Button>
      </PageHeader>

      {summary.length > 0 && (
        <div className="mb-6 grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4" data-testid="time-summary">
          {summary.map((s) => (
            <div key={s.project_id} className="rounded-2xl border border-[#1D225C]/10 bg-white p-4 shadow-sm" data-testid={`time-summary-${s.project_id}`}>
              <p className="truncate text-sm font-semibold text-[#1D225C]">{s.project_name}</p>
              <p className="mt-1 font-display text-xl font-bold text-[#B88A4A]">{fmtMinutes(s.minutes)}</p>
              {s.amount != null && <p className="text-xs text-[#777]">× {eur(s.hourly_rate)}/h = <span className="font-semibold text-[#1D225C]">{eur(s.amount)}</span></p>}
            </div>
          ))}
        </div>
      )}

      {loading ? (
        <div className="h-64 animate-pulse rounded-2xl bg-white" />
      ) : entries.length === 0 ? (
        <EmptyState icon={Clock} title="Noch keine Zeiteinträge" hint="Erfasse Arbeitszeit pro Projekt – bei Stundensatz-Projekten wird das Honorar automatisch berechnet." testid="timeentries-empty">
          <Button data-testid="empty-new-timeentry-button" onClick={() => { setEditing(null); setFormOpen(true); }} className="rounded-xl bg-[#B88A4A] text-white hover:bg-[#a67a3f]">
            <Plus className="mr-1.5 h-4 w-4" /> Zeit erfassen
          </Button>
        </EmptyState>
      ) : (
        <>
          <p className="mb-2 text-sm text-[#777]">Gesamt: <span className="font-bold text-[#1D225C]">{fmtMinutes(totalMinutes)}</span></p>
          <div className="overflow-hidden rounded-2xl border border-[#1D225C]/10 bg-white shadow-sm" data-testid="timeentries-list">
            {entries.map((t, i) => (
              <div key={t.id} data-testid={`timeentry-${t.id}`} className={`flex flex-wrap items-center gap-3 px-4 py-3 ${i ? "border-t border-[#1D225C]/5" : ""}`}>
                <span className="w-24 text-xs text-[#777]">{fmtDate(t.date)}</span>
                <span className="font-display text-sm font-bold text-[#1D225C]">{fmtMinutes(t.duration_minutes)}</span>
                {t.start && t.end && <span className="text-xs text-[#999]">{t.start} – {t.end}</span>}
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-sm text-[#1D225C]">{t.project_name}</span>
                  {t.description && <span className="block truncate text-xs text-[#777]">{t.description}</span>}
                </span>
                <span className="text-xs text-[#999]">{t.user_name}</span>
                <div className="flex gap-1">
                  <button data-testid={`timeentry-edit-${t.id}`} onClick={() => { setEditing(t); setFormOpen(true); }} className="rounded p-1 text-[#999] hover:text-[#1D225C]"><Pencil className="h-3.5 w-3.5" /></button>
                  <button data-testid={`timeentry-delete-${t.id}`} onClick={() => remove(t)} className="rounded p-1 text-[#999] hover:text-red-500"><Trash2 className="h-3.5 w-3.5" /></button>
                </div>
              </div>
            ))}
          </div>
        </>
      )}

      <TimeEntryForm open={formOpen} onOpenChange={setFormOpen} projects={projects} entry={editing} onSaved={load} />
    </div>
  );
}
